import { CustomInputInfo, MenuItemParamInfo } from "@/types";
import { showMenu, sleep } from "@/utils";
import { faCopy, faPaste, faScissors } from "@fortawesome/free-solid-svg-icons";
import { t } from "i18next";
import { ChangeEvent, MouseEvent, useRef } from "react";

function useCustomInput<
	T extends HTMLInputElement | HTMLTextAreaElement,
>(options: {
	extraMenuItems?: MenuItemParamInfo[];
	setValue: (newValue: string) => void;
}): CustomInputInfo<T> {
	const { extraMenuItems, setValue } = options;

	const inputRef = useRef<T>(null);

	const handleInputChange = (event: ChangeEvent<T>): void => {
		setValue(event.currentTarget.value);
	};

	const handleInputContextMenu = (event: MouseEvent<T>): void => {
		event.preventDefault();
		const selection = window.getSelection()?.toString();
		const { value } = event.currentTarget;
		const menuItems: MenuItemParamInfo[] = [
			{
				content: {
					icon: faScissors,
					isDisabled: !selection,
					text: t("cut"),
				},
				onClick: async (): Promise<void> => {
					if (!inputRef.current) {
						return;
					}
					const { selectionStart, selectionEnd } = inputRef.current;
					if (selectionStart === null || selectionEnd === null) {
						return;
					}
					const selectedText = value.substring(
						selectionStart,
						selectionEnd,
					);
					setValue(
						value.substring(0, selectionStart) +
							value.substring(selectionEnd),
					);
					void navigator.clipboard.writeText(selectedText);
					await sleep(1);
					inputRef.current.selectionStart = selectionStart;
				},
				when: true,
			},
			{
				content: {
					icon: faCopy,
					isDisabled: !selection,
					text: t("copy"),
				},
				onClick: (): void => {
					if (!selection) {
						return;
					}
					void navigator.clipboard.writeText(selection);
				},
				when: true,
			},
			{
				content: {
					icon: faPaste,
					text: t("paste"),
				},
				onClick: async (): Promise<void> => {
					if (!inputRef.current) {
						return;
					}
					const clipboardText = await navigator.clipboard.readText();
					const { selectionStart, selectionEnd } = inputRef.current;
					if (selectionStart === null || selectionEnd === null) {
						setValue(clipboardText);
						return;
					}
					setValue(
						value.substring(0, selectionStart) +
							clipboardText +
							value.substring(selectionEnd),
					);
					await sleep(1);
					inputRef.current.selectionStart =
						selectionStart + clipboardText.length;
					inputRef.current.selectionEnd =
						selectionEnd + clipboardText.length;
				},
				when: true,
			},
		];
		if (extraMenuItems) {
			menuItems.push({
				content: {
					divider: true,
				},
				when: true,
			});
			menuItems.push(...extraMenuItems);
		}
		showMenu(event, menuItems, {
			isModal: true,
			shouldBeLighterInDarkTheme: true,
		});
	};

	return {
		onChange: handleInputChange,
		onContextMenu: handleInputContextMenu,
		ref: inputRef,
	};
}

export default useCustomInput;
